import * as React from "react";
import { Wordmark } from "./Wordmark";
import { Button } from "./Button";

export type StatusVariant = "not-found" | "error" | "offline" | "maintenance";

type StatusPageProps = {
  variant: StatusVariant;
  eyebrow: string;
  title: string;
  body: string;
  /** Large ghosted glyph behind the title (e.g. "404"). Decorative; omit for
   *  variants that have no natural code. */
  code?: string;
  cta?: { label: string; onClick: () => void };
};

// Night-sky surface: a moonlight glow over deep shadow-blue with a darkening
// vignette. Expressed as an inline multi-layer gradient — three stacked
// radial-gradients with rgba stops are not reliably emitted by Tailwind's
// arbitrary-value JIT, so this is the allowed "not expressible as a utility"
// case. Colors stay on tokens (var(--primary) / var(--shadow-blue)).
const NIGHT_SKY: React.CSSProperties = {
  background: [
    "radial-gradient(50% 42% at 50% 27%, rgba(255,255,255,0.14), transparent 60%)",
    "radial-gradient(140% 130% at 50% 40%, transparent 38%, rgba(0,0,0,0.5))",
    "radial-gradient(120% 120% at 50% 30%, var(--primary), var(--shadow-blue) 72%)",
  ].join(", "),
};

// Twin soft halos so the brand crest reads as luminous against the dark sky.
const CREST_GLOW: React.CSSProperties = {
  filter:
    "drop-shadow(0 0 14px rgba(255,255,255,0.55)) drop-shadow(0 0 40px rgba(173,205,240,0.45))",
};

// Deterministic starfield: a fixed pseudo-scatter so the layout never shifts
// between renders (Math.random would re-place stars on every paint). Decorative
// only — positions/sizes are intentionally not design tokens, like the cloud
// distribution in the 3D scene.
const STARS = Array.from({ length: 56 }, (_, i) => {
  const size = i % 7 === 0 ? 3 : (i % 3) + 1;
  return {
    top: (i * 61 + 7) % 100,
    left: (i * 37 + 13) % 100,
    size,
    opacity: 0.4 + (i % 5) * 0.14,
    glow: size >= 2,
    duration: 2.4 + (i % 4) * 0.9,
    delay: (i % 6) * 0.5,
  };
});

function Starfield() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {STARS.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white animate-pulse"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            opacity: s.opacity,
            boxShadow: s.glow ? `0 0 ${s.size * 2}px rgba(255,255,255,0.7)` : undefined,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

// "Beyond the veil" status shell: a night-sky surface with a faint starfield,
// a glowing brand crest, a ghosted code behind the title, and a veil divider
// framing the eyebrow. Copy is passed in — the component is i18n-agnostic so it
// tests without translations.
export function StatusPage({ variant, eyebrow, title, body, code, cta }: StatusPageProps) {
  return (
    <div
      role="alert"
      aria-live="polite"
      data-status={variant}
      style={NIGHT_SKY}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-hidden px-6 text-center text-white"
    >
      <Starfield />

      {code ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex select-none items-center justify-center"
        >
          <span
            className="leading-none"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(13rem, 42vh, 34rem)",
              letterSpacing: "0.05em",
              color: "rgba(255,255,255,0.05)",
            }}
          >
            {code}
          </span>
        </div>
      ) : null}

      <div className="relative z-10 flex max-w-xl flex-col items-center gap-7">
        {/* Glowing brand crest. */}
        <div style={CREST_GLOW}>
          <Wordmark size="lg" inverse showTagline={false} />
        </div>

        {/* Veil divider — thin rules + diamonds frame the eyebrow.
            !text-white defeats the design-system .t-eyebrow color leak. */}
        <div className="flex items-center gap-3">
          <span className="h-px w-12 bg-gradient-to-r from-transparent to-white/40" />
          <span className="h-1.5 w-1.5 rotate-45 border border-white/60" />
          <div className="t-eyebrow !text-white/80">{eyebrow}</div>
          <span className="h-1.5 w-1.5 rotate-45 border border-white/60" />
          <span className="h-px w-12 bg-gradient-to-l from-transparent to-white/40" />
        </div>

        {/* !text-white: .t-hero carries color: var(--primary), which would render
            blue-on-blue (invisible) on this surface. */}
        <h1 className="t-hero m-0 !text-white">{title}</h1>
        <p className="t-lede-on-blue">{body}</p>

        {cta ? (
          <Button variant="outline" surface="dark" onClick={cta.onClick}>
            {cta.label}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
